import { Link, useLocation } from "react-router-dom";

import { BrandLogo } from "./BrandLogo";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSubscriptionPriceText } from "../hooks/usePrice";
import { useLanguage, useT } from "../i18n/LanguageProvider";
import { C, font } from "../styles/tokens";
import { LEGAL_CONFIG } from "../config/legalConfig";
import { isAppView, visitStorage } from "../utils/appView";

// =============================================================================
// PublicShell -- shared chrome for every public marketing page (landing,
// for-businesses, for-institutions, shop / coming-soon).
//
// Header layout mirrors ID.me: a slim tabs strip on top ("Shop", "User
// login" -- plus the corner links on mobile, where the main bar has no room),
// then the main sticky bar with a large logo, "For Businesses" / "For
// Academic Institutions" on the right corner, language toggle, and Sign in.
// =============================================================================

export function PublicShell({ children }) {
  // Inside the mobile app (?view=app): the page alone (utils/appView.js).
  const { search } = useLocation();
  const appView = isAppView(search, visitStorage());
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: font,
        color: C.textDark,
        background: "#fff",
      }}
    >
      {appView ? null : <PublicHeader />}
      <main style={{ flex: 1 }}>{children}</main>
      {appView ? null : <PublicFooter />}
    </div>
  );
}

export function PublicHeader() {
  const t = useT();
  const { lang, setLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const location = useLocation();

  const tabs = [
    { to: "/shop", label: t("pub.shopTab") },
    { to: "/gateway", label: t("pub.gatewayTab") },
    { to: "/login", label: t("pub.loginTab") },
  ];
  const cornerLinks = [
    { to: "/business", label: t("pub.forBusinesses") },
    { to: "/institutions", label: t("pub.forInstitutions") },
  ];
  // The main bar can't fit the corner links on a phone -- fold them into the
  // (horizontally scrollable) tabs strip instead so every page stays reachable.
  const stripItems = isMobile ? [...tabs, ...cornerLinks] : tabs;

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 150,
        background: C.bg,
        boxShadow: "0 1px 6px rgba(22,41,76,0.06)",
      }}
    >
      {/* Tabs strip */}
      <nav
        style={{
          display: "flex",
          gap: 2,
          padding: isMobile ? "0 12px" : "0 28px",
          borderBottom: `1px solid ${C.divider}`,
          overflowX: "auto",
        }}
      >
        {stripItems.map((tab) => {
          const active = location.pathname === tab.to;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              style={{
                padding: "9px 14px",
                fontSize: 13,
                fontWeight: 700,
                whiteSpace: "nowrap",
                textDecoration: "none",
                color: active ? C.primary : C.textMed,
                borderBottom: active
                  ? `2px solid ${C.primary}`
                  : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Main bar -- big logo left, corner links + language + sign in right */}
      <div
        style={{
          padding: isMobile ? "12px 16px" : "14px 32px",
          borderBottom: `1px solid ${C.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <BrandLogo to="/" mark={isMobile ? 30 : 38} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 8 : 20,
          }}
        >
          {!isMobile &&
            cornerLinks.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    textDecoration: "none",
                    color: active ? C.primary : C.navy,
                    borderBottom: active
                      ? `2px solid ${C.primary}`
                      : "2px solid transparent",
                    paddingBottom: 2,
                  }}
                >
                  {l.label}
                </Link>
              );
            })}
          <button
            onClick={() => setLanguage(lang === "en" ? "ta" : "en")}
            style={{
              padding: "7px 13px",
              fontSize: 13,
              fontWeight: 700,
              color: C.textDark,
              background: "transparent",
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: font,
              letterSpacing: 0.5,
            }}
          >
            {lang === "en" ? "த" : "EN"}
          </button>
          <Link
            to="/login"
            style={{
              color: "#fff",
              background: C.primary,
              textDecoration: "none",
              fontSize: 15,
              fontWeight: 700,
              padding: "10px 22px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            {t("common.signIn")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PublicFooter() {
  const t = useT();
  const subscriptionText = useSubscriptionPriceText();
  const year = new Date().getFullYear();
  const isMobile = useIsMobile();

  return (
    <footer
      style={{
        padding: isMobile ? "24px 16px" : "36px 40px",
        borderTop: `1px solid ${C.divider}`,
        background: C.bg,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "flex-end",
          gap: isMobile ? 24 : 32,
        }}
      >
        {/* Bottom Corner Left: Logo, Company Name, Address */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxWidth: 420,
          }}
        >
          <div>
            <BrandLogo to="/" mark={24} withTagline />
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.navy,
              letterSpacing: "-0.2px",
            }}
          >
            {LEGAL_CONFIG.companyName}
          </div>
          <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.5 }}>
            {LEGAL_CONFIG.businessAddress}
          </div>
        </div>

        {/* Middle: Copyright Notice */}
        <div
          style={{
            fontSize: 12,
            color: C.textLight,
            textAlign: isMobile ? "left" : "center",
            paddingBottom: isMobile ? 0 : 2,
          }}
        >
          TickAndPass &copy; {year}
        </div>

        {/* Bottom Right: Footer Links & Support */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "flex-start" : "flex-end",
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: C.textMed,
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <Link to="/terms-and-conditions" style={footerLink}>
              {t("pub.termsAndConditions")}
            </Link>
            <Link to="/privacy-policy" style={footerLink}>
              {t("pub.privacyPolicy")}
            </Link>
          </div>
          {subscriptionText && (
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
              Subscription: {subscriptionText}
            </div>
          )}

          <div style={{ fontSize: 12, color: C.textLight }}>
            <a
              href="mailto:support@tickandpass.com"
              style={{
                color: C.textMed,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              support@tickandpass.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

const footerLink = {
  color: C.textMed,
  textDecoration: "none",
  fontWeight: 600,
  fontSize: 13,
};

/** Uppercase section label used above headlines on the marketing pages. */
export function Eyebrow({ color = C.primary, children }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 800,
        color,
        textTransform: "uppercase",
        letterSpacing: 1,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Pre-launch stand-in for the partner marquee: same band, no names. Swap
 * back to <LogoMarquee> with real partner names once onboarding starts --
 * never list companies/institutions that haven't actually joined.
 */
export function PartnersComingBand({ title }) {
  const t = useT();
  return (
    <section
      style={{
        background: C.surface,
        borderTop: `1px solid ${C.divider}`,
        borderBottom: `1px solid ${C.divider}`,
        padding: "30px 20px",
        textAlign: "center",
      }}
    >
      {title && (
        <div
          style={{
            marginBottom: 8,
            fontSize: 12,
            fontWeight: 700,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {title}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 600, color: C.textMed }}>
        {t("pub.partnersComing")}
      </div>
    </section>
  );
}

/**
 * Auto-scrolling strip of partner wordmarks. The row is rendered twice for a
 * seamless loop; animation lives in responsive.css (.tp-marquee). Currently
 * unused pre-launch -- see PartnersComingBand above.
 */
export function LogoMarquee({ title, items }) {
  const row = (key) => (
    <div
      key={key}
      style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
    >
      {items.map((name) => (
        <span
          key={`${key}-${name}`}
          style={{
            padding: "0 32px",
            fontSize: 20,
            fontWeight: 800,
            whiteSpace: "nowrap",
            color: C.textLight,
            letterSpacing: -0.3,
          }}
        >
          {name}
        </span>
      ))}
    </div>
  );
  return (
    <section
      style={{
        background: C.surface,
        borderTop: `1px solid ${C.divider}`,
        borderBottom: `1px solid ${C.divider}`,
        padding: "28px 0",
        overflow: "hidden",
      }}
    >
      {title && (
        <div
          style={{
            textAlign: "center",
            marginBottom: 18,
            fontSize: 12,
            fontWeight: 700,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            padding: "0 20px",
          }}
        >
          {title}
        </div>
      )}
      <div className="tp-marquee">
        {row("a")}
        {row("b")}
      </div>
    </section>
  );
}
